<script setup lang="ts">
import { inject, onMounted, ref } from 'vue';
import KgrAccordion from '../../components/common/accordion/Accordion.vue';
import KgrAccordionItem from '../../components/common/accordion/AccordionItem.vue';
import KgrCheckbox from '../../components/common/form/checkbox/Checkbox.vue';
import KgrFormScaffold from '../../components/common/form/FormScaffold.vue';
import KgrLoading from '../../components/common/Loading.vue';
import type { Notify } from '@/composables/notifications';
import ProblemVisibilityRepository from '../../js/repository/ProblemVisibilityRepository.js';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = defineProps<{
    clubId: number
}>();

interface Visibility {
    problem: string
    category: string
    visible: boolean
}

const notify = inject<Notify>('notify')!;
const loading = ref(true);
const pending = ref(false);
const visibilitiesByCategory = ref<Record<string, Visibility[]>>({});

onMounted(() => {
    load();
});

function load() {
    loading.value = true;
    ProblemVisibilityRepository.findByClub(props.clubId)
        .then((data: Visibility[]) => {
            const byCategory: Record<string, Visibility[]> = {};
            for (const visibility of data) {
                if (!byCategory[visibility.category]) {
                    byCategory[visibility.category] = [];
                }
                byCategory[visibility.category].push({
                    problem: visibility.problem,
                    category: visibility.category,
                    visible: visibility.visible,
                });
            }
            visibilitiesByCategory.value = byCategory;
        })
        .catch(() => notify.error('error.loadingData'))
        .finally(() => loading.value = false);
}

function update() {
    pending.value = true;
    const visibilities = Object.values(visibilitiesByCategory.value)
        .flat()
        .map((visibility) => ({ problem: visibility.problem, visible: visibility.visible }));
    ProblemVisibilityRepository.updateForClub(props.clubId, visibilities)
        .then(() => notify.success('saveChanges.success'))
        .catch(() => notify.error('error.loadingData'))
        .finally(() => pending.value = false);
}
</script>

<template>
    <kgr-form-scaffold
        :pending="loading || pending"
        :with-back-button="false"
        @primaryBtnClicked="update"
    >
        <kgr-loading v-if="loading" />
        <template v-else>
            <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                {{ $t('club.hintOptions.headline') }}
            </h3>
            <kgr-accordion>
                <kgr-accordion-item
                    v-for="(visibilities, category) in visibilitiesByCategory"
                    :key="category"
                    :title="$t(`problem.category.${category}`)"
                >
                    <div
                        v-for="visibility in visibilities"
                        :key="visibility.problem"
                        class="kgr-row"
                    >
                        <kgr-checkbox
                            v-model="visibility.visible"
                            :label="$t(`hint.visibility.${visibility.problem}`)"
                        />
                    </div>
                </kgr-accordion-item>
            </kgr-accordion>
        </template>
    </kgr-form-scaffold>
</template>

<style scoped lang="scss" rel="stylesheet/scss"></style>
